import { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { translations } from '../i18n/translations';
import { makeLocalize, onLocalizeUpdate } from '../i18n/localize';

const LanguageContext = createContext(null);

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * 7 languages supported:
 *   ar (RTL) · en · es · hi · ur (RTL) · fr · ru
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'ar', label: 'العربية', flag: 'ae', dir: 'rtl', english: 'Arabic'  },
  { code: 'en', label: 'English',  flag: 'gb', dir: 'ltr', english: 'English' },
  { code: 'es', label: 'Español',  flag: 'es', dir: 'ltr', english: 'Spanish' },
  { code: 'hi', label: 'हिन्दी',    flag: 'in', dir: 'ltr', english: 'Hindi'   },
  { code: 'ur', label: 'اردو',      flag: 'pk', dir: 'rtl', english: 'Urdu'    },
  { code: 'fr', label: 'Français', flag: 'fr', dir: 'ltr', english: 'French'  },
  { code: 'ru', label: 'Русский',  flag: 'ru', dir: 'ltr', english: 'Russian' },
];
const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map(l => l.code);
const DIR_BY_CODE = Object.fromEntries(SUPPORTED_LANGUAGES.map(l => [l.code, l.dir]));

const detectLanguage = () => {
  if (typeof window === 'undefined') return 'ar';

  // 1. Saved choice
  const stored = localStorage.getItem('language');
  if (stored && SUPPORTED_CODES.includes(stored)) return stored;

  // 2. URL override (?lang=fr)
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl && SUPPORTED_CODES.includes(fromUrl)) return fromUrl;
  } catch { /* ignore */ }

  // 3. Browser preferences — match by 2-letter prefix
  const prefs = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language || ''];
  for (const code of prefs) {
    const lower = (code || '').toLowerCase();
    for (const sc of SUPPORTED_CODES) {
      if (lower.startsWith(sc)) return sc;
    }
  }

  return 'ar';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(detectLanguage);
  const [autoDetected, setAutoDetected] = useState(() => !localStorage.getItem('language'));
  const [, setTranslationTick] = useState(0);   // bumped when async translations arrive
  const localizeRef = useRef(makeLocalize(language, API_BASE));

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.dir = DIR_BY_CODE[language] || 'ltr';
    document.documentElement.lang = language;
    localizeRef.current = makeLocalize(language, API_BASE);
    setTranslationTick((n) => n + 1);    // re-render so children pick up new localize
  }, [language]);

  // Re-render whenever the auto-translate cache is updated by an async fetch.
  useEffect(() => {
    return onLocalizeUpdate(() => setTranslationTick((n) => n + 1));
  }, []);

  const t = (key) => {
    return translations[language]?.[key]
      || translations['en']?.[key]
      || translations['ar']?.[key]
      || key;
  };

  // Stable function reference per render — language captured via ref above.
  const localize = useMemo(() => (opts) => localizeRef.current(opts), [language]);

  /**
   * Toggle behaviour preserved for legacy callers (ar ↔ en).
   * Use `setLanguage(code)` to pick a specific code from the 7 supported.
   */
  const toggleLanguage = () => {
    setAutoDetected(false);
    setLanguage((prev) => (prev === 'ar' ? 'en' : 'ar'));
  };

  const pickLanguage = (code) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    setAutoDetected(false);
    setLanguage(code);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        toggleLanguage,
        pickLanguage,
        t,
        localize,
        autoDetected,
        supportedLanguages: SUPPORTED_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
